import PermissionService from 'components/Common/PermissionService';
import { CleanRoomTypes } from 'components/CleanRooms/constants';
import { userTypes } from 'utils/appConstants';

// Billable-supported clean room type keys (short form used in
// question.cleanRoomTypes)
const BILLABLE_SUPPORTED_TYPE_KEYS = ['HybridV2', 'HabuConfidentialComputing'];

/**
 * Normalize clean room type keys to their string form
 * Handles both string arrays ['HybridV2'] and object arrays [{name: 'HybridV2'}]
 *
 * @param {string[]|Object[]} [cleanRoomTypes] - Array of clean room types
 * @returns {string[]} Array of normalized type keys
 */
export const normalizeCleanRoomTypeKeys = (cleanRoomTypes) => {
  if (!cleanRoomTypes?.length) {
    return [];
  }
  return cleanRoomTypes.map(
    type => (typeof type === 'string' ? type : type?.name),
  );
};

/**
 * Check if the current user has permission to see billable features
 * This is the single unified function to manage all aspects of billing
 * feature visibility
 *
 * @param {string} [cleanRoomType] - Optional clean room type to check
 * @returns {boolean} True if:
 *   1. User is CXUser or Superuser, AND
 *   2. If cleanRoomType provided, it must be HYBRID_V2_CLEAN_ROOM or
 *      HABU_CONFIDENTIAL_COMPUTING_CLEAN_ROOM
 */
export const canSeeBillableFeatures = (cleanRoomType) => {
  // Check user permissions first
  const isCXUser = PermissionService.isCXUser();
  const isSuperUser = PermissionService.isAuthorized([userTypes.superuser]);
  const hasPermission = isCXUser || isSuperUser;

  // If no clean room type provided, just return permission check
  if (!cleanRoomType) {
    return hasPermission;
  }

  // Check if clean room type supports billable features
  const isSupportedType = cleanRoomType === CleanRoomTypes.HYBRID_V2_CLEAN_ROOM
    || cleanRoomType === CleanRoomTypes.HABU_CONFIDENTIAL_COMPUTING_CLEAN_ROOM;

  return hasPermission && isSupportedType;
};

/**
 * Check if any of the compatible clean room types support billable features
 * Used for standalone questions that aren't tied to a specific clean room yet
 *
 * @param {string[]|Object[]} [compatibleCleanRoomTypes] - Array of
 * compatible clean room types (strings or objects with 'name' property)
 * @returns {boolean} True if user has permission AND at least one compatible
 * type supports billable
 */
export const hasAnyBillableCompatibleCleanRooms = (compatibleCleanRoomTypes) => {
  // Check user permissions first
  const isCXUser = PermissionService.isCXUser();
  const isSuperUser = PermissionService.isAuthorized([userTypes.superuser]);
  const hasPermission = isCXUser || isSuperUser;

  if (!hasPermission || !compatibleCleanRoomTypes?.length) {
    return false;
  }

  // Normalize to strings (handle both ['HybridV2'] and [{name: 'HybridV2'}])
  const typeKeys = normalizeCleanRoomTypeKeys(compatibleCleanRoomTypes);

  // Check if any compatible type is billable-supported
  return typeKeys.some(
    typeKey => BILLABLE_SUPPORTED_TYPE_KEYS.includes(typeKey),
  );
};
